<template>
  <div class="cpp-root">

    <!-- ══ HEADER ════════════════════════════════════════ -->
    <div class="cpp-header">
      <div class="cpp-header-left">
        <span class="cpp-beacon">
          <span class="cpp-beacon-ring"></span>
          <v-icon size="13" color="#fff">mdi-radar</v-icon>
        </span>
        <div class="cpp-header-text">
          <span class="cpp-title">Priority Watch</span>
          <span class="cpp-subtitle">
            {{ criticalIssues.length }} critical item{{ criticalIssues.length !== 1 ? 's' : '' }} flagged
          </span>
        </div>
      </div>

      <div class="cpp-header-right">
        <span class="cpp-live">
          <span class="cpp-live-dot"></span>LIVE
        </span>
        <button class="cpp-nav" :disabled="criticalIssues.length < 2" @click="prev" aria-label="Previous">
          <v-icon size="13">mdi-chevron-left</v-icon>
        </button>
        <button class="cpp-nav" :disabled="criticalIssues.length < 2" @click="next" aria-label="Next">
          <v-icon size="13">mdi-chevron-right</v-icon>
        </button>
      </div>
    </div>

    <!-- ══ STAGE ═══════════════════════════════════════════ -->
    <div class="cpp-stage" v-if="criticalIssues.length">

      <transition name="cpp-fade" mode="out-in">
        <div
          class="cpp-card"
          :class="[`cpp-card--${active.priority}`, { 'cpp-card--overdue': isOverdue(active) }]"
          :key="activeIndex"
          @mouseenter="hovered = true"
          @mouseleave="hovered = false"
        >
          <span class="cpp-corner cpp-corner--tl"></span>
          <span class="cpp-corner cpp-corner--br"></span>

          <!-- head row: index / stamp / flag / stage -->
          <div class="cpp-card-head">
            <span class="cpp-index">{{ pad(activeIndex + 1) }}<i>/{{ pad(criticalIssues.length) }}</i></span>

            <span class="cpp-stamp" :class="`cpp-stamp--${active.priority}`">
              {{ active.priority }}
            </span>

            <span v-if="isOverdue(active)" class="cpp-flag">
              <v-icon size="9" color="#fff">mdi-clock-alert-outline</v-icon> overdue
            </span>

            <span class="cpp-stage-tag" :class="`cpp-stage--${stageCode(active.stage_name)}`">
              {{ active.stage_name }}
            </span>
          </div>

          <!-- name -->
          <div class="cpp-name" :title="active.name">{{ active.name }}</div>

          <!-- foot: ref / account / date / project -->
          <div class="cpp-card-foot">
            <span class="cpp-ref">{{ active.reference_num }}</span>

            <template v-if="active.account_name">
              <span class="cpp-dot-sep"></span>
              <span class="cpp-meta">
                <v-icon size="10" color="#7c74a0">mdi-domain</v-icon>
                {{ active.account_name }}
              </span>
            </template>

            <template v-if="active.end_date">
              <span class="cpp-dot-sep"></span>
              <span class="cpp-meta" :class="{ 'cpp-meta--overdue': isOverdue(active) }">
                <v-icon size="10" :color="isOverdue(active) ? '#7c3aed' : '#7c74a0'">mdi-calendar-clock</v-icon>
                {{ formatDate(active.end_date) }}
              </span>
            </template>

            <template v-if="active.project_name">
              <span class="cpp-dot-sep"></span>
              <span class="cpp-meta">
                <v-icon size="10" color="#7c74a0">mdi-briefcase-outline</v-icon>
                {{ active.project_name }}
              </span>
            </template>
          </div>
        </div>
      </transition>

      <!-- segmented tick nav / progress -->
      <div class="cpp-ticks" v-if="criticalIssues.length > 1">
        <button
          v-for="(_, i) in criticalIssues"
          :key="i"
          class="cpp-tick"
          :class="{ 'cpp-tick--done': i < activeIndex }"
          @click="goTo(i)"
          :aria-label="`Go to item ${i + 1}`"
        >
          <span
            v-if="i === activeIndex"
            class="cpp-tick-fill"
            :style="{ animationDuration: cycleDuration + 'ms', animationPlayState: hovered ? 'paused' : 'running' }"
          ></span>
        </button>
      </div>

    </div>

    <!-- ══ EMPTY STATE ═════════════════════════════════════ -->
    <div class="cpp-empty" v-else>
      <span class="cpp-empty-mark">✓</span>
      <span>All clear — no critical issues right now</span>
    </div>

  </div>
</template>

<script>
import moment from 'moment';
import IssueService from '@/api/service/Issue/IssueService';

const CYCLE_MS = 4000;

export default {
  name: 'CriticalPriorityPanel',

  props: {
    currentIssueId: { type: [Number, String], default: null },
  },

  data() {
    return {
      criticalIssues: [],
      activeIndex: 0,
      hovered: false,
      cycleDuration: CYCLE_MS,
      $timer: null,
    };
  },

  computed: {
    active() {
      return this.criticalIssues[this.activeIndex] || {};
    },
  },

  async created() {
    await this.loadCriticalIssues();
    this.startCycle();
  },

  beforeUnmount() {
    clearInterval(this.$timer);
  },

  methods: {
    async loadCriticalIssues() {
      try {
        const userData = JSON.parse(localStorage.getItem('user_details') || '{}');
        const res = await IssueService.getAssignToMeData({ next_action: userData.id });
        if (!res || !res.length) return;
        this.criticalIssues = res.filter(i =>
          i.priority === 'critical' &&
          String(i.id) !== String(this.currentIssueId)
        );
      } catch (e) {
        console.error('CriticalPriorityPanel:', e);
      }
    },

    startCycle() {
      this.$timer = setInterval(() => {
        if (!this.hovered && this.criticalIssues.length > 1) this.next();
      }, CYCLE_MS);
    },

    next() {
      this.activeIndex = (this.activeIndex + 1) % this.criticalIssues.length;
    },
    prev() {
      this.activeIndex = (this.activeIndex - 1 + this.criticalIssues.length) % this.criticalIssues.length;
    },
    goTo(i) {
      this.activeIndex = i;
    },

    isOverdue(issue) {
      return !!(issue.end_date && moment(issue.end_date).isBefore(moment()));
    },
    stageCode(name) {
      return name ? name.replaceAll(' ', '').toLowerCase() : '';
    },
    formatDate(d) {
      return moment(d).format('DD MMM YYYY');
    },
    pad(n) {
      return String(n).padStart(2, '0');
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@500;600;700&display=swap');

/* ── TOKENS ──────────────────────────────────────────── */
.cpp-root {
  --cpp-ink:          #1a1730;
  --cpp-ink-soft:     #7c74a0;
  --cpp-paper:        rgba(255, 255, 255, 0.55);
  --cpp-paper-raised: rgba(255, 255, 255, 0.72);
  --cpp-line:         rgba(180, 160, 220, 0.28);
  --cpp-line-strong:  rgba(150, 120, 200, 0.42);
  --cpp-accent:       #7c3aed;
  --cpp-accent-soft:  rgba(124, 58, 237, 0.12);
  --cpp-alarm:        #9b1dea;
  --cpp-alarm-soft:   rgba(155, 29, 234, 0.12);
  --cpp-amber:        #7c3aed;
  --cpp-amber-soft:   rgba(124, 58, 237, 0.1);

  width: 100%;
  border-radius: 14px;
  overflow: hidden;
  background: var(--cpp-paper-raised);
  backdrop-filter: blur(18px) saturate(1.6);
  -webkit-backdrop-filter: blur(18px) saturate(1.6);
  border: 1px solid rgba(200, 180, 255, 0.38);
  box-shadow:
    0 4px 24px rgba(124, 58, 237, 0.1),
    0 1px 0 rgba(255, 255, 255, 0.7) inset;
  font-family: 'Inter', system-ui, sans-serif;
}

/* ── HEADER ──────────────────────────────────────────── */
.cpp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px 11px;
  background: var(--cpp-paper);
  border-bottom: 1px solid var(--cpp-line);
}

.cpp-header-left  { display: flex; align-items: center; gap: 10px; }
.cpp-header-right { display: flex; align-items: center; gap: 6px; }

.cpp-beacon {
  position: relative;
  width: 28px; height: 28px; border-radius: 8px;
  background: linear-gradient(135deg, #7c3aed, #a855f7);
  box-shadow: 0 2px 10px rgba(124, 58, 237, 0.4);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.cpp-beacon-ring {
  position: absolute; inset: -4px; border-radius: 11px;
  border: 1.5px solid rgba(168, 85, 247, 0.7);
  animation: cpp-ping 2.4s ease-out infinite;
}
@keyframes cpp-ping {
  0%   { transform: scale(0.85); opacity: 0.65; }
  70%  { transform: scale(1.3);  opacity: 0; }
  100% { transform: scale(1.3);  opacity: 0; }
}

.cpp-header-text { display: flex; flex-direction: column; gap: 1px; }
.cpp-title {
  font-size: 11px; font-weight: 800; color: var(--cpp-ink);
  letter-spacing: 0.6px; text-transform: uppercase;
}
.cpp-subtitle { font-size: 10px; color: var(--cpp-ink-soft); font-weight: 500; }

.cpp-live {
  display: inline-flex; align-items: center; gap: 5px;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 9px; font-weight: 700; letter-spacing: 1.4px;
  color: var(--cpp-ink-soft);
}
.cpp-live-dot {
  width: 5px; height: 5px; border-radius: 50%;
  background: var(--cpp-accent);
  animation: cpp-live 1.8s ease-out infinite;
}
@keyframes cpp-live {
  0%   { box-shadow: 0 0 0 0   rgba(124, 58, 237, 0.55); }
  70%  { box-shadow: 0 0 0 5px rgba(124, 58, 237, 0); }
  100% { box-shadow: 0 0 0 0   rgba(124, 58, 237, 0); }
}

.cpp-nav {
  width: 24px; height: 24px; border-radius: 6px;
  border: 1px solid var(--cpp-line-strong);
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: var(--cpp-ink-soft);
  transition: border-color 0.15s, color 0.15s, background 0.15s;
}
.cpp-nav:hover:not(:disabled) {
  border-color: var(--cpp-accent);
  color: var(--cpp-accent);
  background: rgba(124, 58, 237, 0.08);
}
.cpp-nav:disabled { opacity: 0.35; cursor: default; }

/* ── STAGE ───────────────────────────────────────────── */
.cpp-stage {
  padding: 12px 14px 13px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* ── CARD ────────────────────────────────────────────── */
.cpp-card {
  position: relative;
  padding: 13px 14px 11px;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(196, 174, 255, 0.35);
  border-radius: 10px;
  box-shadow:
    0 2px 12px rgba(124, 58, 237, 0.08),
    0 1px 0 rgba(255, 255, 255, 0.8) inset;
}

.cpp-corner { position: absolute; width: 9px; height: 9px; pointer-events: none; }
.cpp-corner--tl { top: -1px; left: -1px; border-top: 2px solid var(--cpp-line-strong); border-left: 2px solid var(--cpp-line-strong); }
.cpp-corner--br { bottom: -1px; right: -1px; border-bottom: 2px solid var(--cpp-line-strong); border-right: 2px solid var(--cpp-line-strong); }
.cpp-card--critical .cpp-corner { border-color: var(--cpp-accent); }
.cpp-card--high .cpp-corner     { border-color: rgba(168, 85, 247, 0.6); }

/* head row */
.cpp-card-head { display: flex; align-items: center; gap: 7px; flex-wrap: wrap; }

.cpp-index {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 13px; font-weight: 700; color: var(--cpp-ink);
  flex-shrink: 0;
}
.cpp-index i {
  font-style: normal; font-size: 10px; font-weight: 600;
  color: var(--cpp-ink-soft); margin-left: 1px;
}

.cpp-stamp {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 9px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase;
  padding: 2px 8px; border: 1.5px dashed currentColor; border-radius: 3px;
  transform: rotate(-3deg);
  flex-shrink: 0;
}
.cpp-stamp--critical { color: var(--cpp-accent); }
.cpp-stamp--high     { color: #a855f7; }

.cpp-flag {
  display: inline-flex; align-items: center; gap: 3px;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 9px; font-weight: 700; letter-spacing: 0.4px; text-transform: uppercase;
  color: #fff;
  background: linear-gradient(90deg, #7c3aed, #a855f7);
  padding: 2px 7px; border-radius: 3px; flex-shrink: 0;
  box-shadow: 0 1px 6px rgba(124, 58, 237, 0.4);
}

.cpp-stage-tag {
  margin-left: auto;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 9px; font-weight: 600; letter-spacing: 0.4px; text-transform: uppercase;
  padding: 2px 8px; border-radius: 3px;
  border: 1px solid var(--cpp-line-strong);
  color: var(--cpp-ink-soft);
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  flex-shrink: 0;
}
.cpp-stage--inprogress       { color: #1d4ed8; border-color: rgba(29,78,216,0.3);   background: rgba(29,78,216,0.07); }
.cpp-stage--open             { color: #2563eb; border-color: rgba(37,99,235,0.3);   background: rgba(37,99,235,0.07); }
.cpp-stage--pending          { color: #ca8a04; border-color: rgba(202,138,4,0.3);   background: rgba(202,138,4,0.07); }
.cpp-stage--closed           { color: var(--cpp-ink-soft); border-color: var(--cpp-line-strong); background: rgba(255,255,255,0.4); }
.cpp-stage--completed        { color: #16a34a; border-color: rgba(22,163,74,0.3);   background: rgba(22,163,74,0.07); }
.cpp-stage--nextaction       { color: #7c3aed; border-color: rgba(124,58,237,0.3);  background: rgba(124,58,237,0.07); }
.cpp-stage--nextactiontibura { color: #7c3aed; border-color: rgba(124,58,237,0.3);  background: rgba(124,58,237,0.07); }
.cpp-stage--nextactioncf     { color: #7c3aed; border-color: rgba(124,58,237,0.3);  background: rgba(124,58,237,0.07); }
.cpp-stage--qapending        { color: #c2410c; border-color: rgba(194,65,12,0.3);   background: rgba(194,65,12,0.07); }
.cpp-stage--qacompleted      { color: #15803d; border-color: rgba(21,128,61,0.3);   background: rgba(21,128,61,0.07); }
.cpp-stage--hold             { color: #a16207; border-color: rgba(161,98,7,0.3);    background: rgba(161,98,7,0.07); }
.cpp-stage--cancel           { color: #be123c; border-color: rgba(190,18,60,0.3);   background: rgba(190,18,60,0.07); }
.cpp-stage--clarification    { color: #ea580c; border-color: rgba(234,88,12,0.3);   background: rgba(234,88,12,0.07); }
.cpp-stage--assigned         { color: #059669; border-color: rgba(5,150,105,0.3);   background: rgba(5,150,105,0.07); }
.cpp-stage--receivedreply    { color: #0284c7; border-color: rgba(2,132,199,0.3);   background: rgba(2,132,199,0.07); }
.cpp-stage--itsupportrequired{ color: #9333ea; border-color: rgba(147,51,234,0.3);  background: rgba(147,51,234,0.07); }
.cpp-stage--errorlog         { color: #dc2626; border-color: rgba(220,38,38,0.3);   background: rgba(220,38,38,0.07); }
.cpp-stage--workorder        { color: #7e22ce; border-color: rgba(126,34,206,0.3);  background: rgba(126,34,206,0.07); }
.cpp-stage--nsa              { color: #b45309; border-color: rgba(180,83,9,0.3);    background: rgba(180,83,9,0.07); }
.cpp-stage--wfa              { color: #3b82f6; border-color: rgba(59,130,246,0.3);  background: rgba(59,130,246,0.07); }

/* name */
.cpp-name {
  font-size: 14px; font-weight: 700; color: var(--cpp-ink);
  line-height: 1.35; margin: 9px 0 10px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

/* foot */
.cpp-card-foot {
  display: flex; align-items: center; gap: 6px; flex-wrap: nowrap; overflow: hidden;
  padding-top: 9px; border-top: 1px dashed rgba(180, 160, 220, 0.4);
}
.cpp-ref {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 10px; font-weight: 700; color: var(--cpp-ink-soft);
  flex-shrink: 0;
}
.cpp-meta {
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 10.5px; font-weight: 500; color: var(--cpp-ink-soft);
  white-space: nowrap; flex-shrink: 0; overflow: hidden; text-overflow: ellipsis;
}
.cpp-meta--overdue { color: var(--cpp-accent); font-weight: 700; }

.cpp-dot-sep {
  width: 2.5px; height: 2.5px; border-radius: 50%;
  background: var(--cpp-line-strong); flex-shrink: 0;
}

/* ── TICK NAV / PROGRESS ─────────────────────────────── */
.cpp-ticks { display: flex; align-items: center; gap: 3px; }
.cpp-tick {
  flex: 1; height: 3px; padding: 0; margin: 0; border: none; border-radius: 2px;
  background: rgba(180, 160, 220, 0.25); position: relative; overflow: hidden; cursor: pointer;
}
.cpp-tick--done { background: linear-gradient(90deg, #7c3aed, #a855f7); }
.cpp-tick-fill {
  position: absolute; inset: 0; width: 0%;
  background: linear-gradient(90deg, #7c3aed, #a855f7);
  animation: cpp-tick-fill linear forwards;
}
@keyframes cpp-tick-fill { from { width: 0%; } to { width: 100%; } }

/* ── EMPTY ───────────────────────────────────────────── */
.cpp-empty {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 22px 14px;
  font-size: 11.5px; font-weight: 600; color: var(--cpp-ink-soft);
  background: var(--cpp-paper);
}
.cpp-empty-mark {
  width: 18px; height: 18px; border-radius: 50%;
  background: rgba(58, 125, 79, 0.1); color: #3a7d4f;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 800;
}

/* ── CROSSFADE TRANSITION ────────────────────────────── */
.cpp-fade-enter-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.cpp-fade-leave-active { transition: opacity 0.18s ease, transform 0.18s ease; }
.cpp-fade-enter-from   { opacity: 0; transform: translateY(5px); }
.cpp-fade-leave-to     { opacity: 0; transform: translateY(-4px); }

/* ── REDUCED MOTION ──────────────────────────────────── */
@media (prefers-reduced-motion: reduce) {
  .cpp-beacon-ring, .cpp-live-dot, .cpp-tick-fill { animation: none; }
  .cpp-fade-enter-active, .cpp-fade-leave-active { transition: none; }
}
</style>